//https://boost.allnetru.dev/api/documentation/organizer#/Sponsors
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  GetOrganizerPartnersByEventFiltersResponse,
  GetOrganizerPartnersByEventResponse,
  GetOrganizerPartnersFiltersResponse,
  PatchOrganizerPartnersStatusByIdResponse,
} from './models';
import { API_EVENT_TAGS } from '../../tags';
import { FiltersParams } from '../../models';
import { getQueryStringFromParams } from 'lib.shared/src/utils';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';

export const organizerPartners = createApi({
  reducerPath: API_EVENT_TAGS.ORGANIZER_PARTNERS,
  baseQuery: baseQueryWithReauth,
  tagTypes: [API_EVENT_TAGS.ORGANIZER_PARTNERS],
  endpoints: (build) => ({
    //https://boost.allnetru.dev/api/documentation/organizer#/Sponsors/get_api_organizer_events__event__partners
    getOrganizerPartnersByEvent: build.query<
      GetOrganizerPartnersByEventResponse,
      {
        eventId: string;
        page?: number;
        perpage?: number;
        filters?: {
          statuses?: string[];
        } | null;
      }
    >({
      query: ({ eventId, page, filters, perpage = 20 }) => {
        const params = new URLSearchParams();
        if (page) {
          params.append('page', page.toString());
        }
        if (perpage) {
          params.append('perpage', perpage.toString());
        }
        if (Array.isArray(filters?.statuses)) {
          filters.statuses.forEach((id) => {
            params.append('filters[statuses][]', id.toString());
          });
        }
        const queryString = params.toString();
        return {
          url: `organizer/events/${eventId}/partners${queryString ? '?' + queryString : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_PARTNERS],
    }),
    patchOrganizerPartnersStatusById: build.mutation<
      PatchOrganizerPartnersStatusByIdResponse,
      { eventId: string; statusId: number; invite: number; transition: number }
    >({
      query: ({ eventId, statusId, invite, transition }) => ({
        method: 'PATCH',
        url: `organizer/events/${eventId}/invites/${invite}/status/${statusId}`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
        body: { transition },
      }),
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_PARTNERS],
    }),
    getOrganizerPartnersByEventFilters: build.query<
      GetOrganizerPartnersByEventFiltersResponse,
      { eventId?: string; search?: string; page?: number }
    >({
      query: ({ eventId, page, search }) => {
        const params = new URLSearchParams();

        if (page) {
          params.append('page', page.toString());
        }
        if (search) {
          params.append('search', search);
        }
        const queryString = params.toString();

        return {
          url: `organizer/events/${eventId!}/partners/filter${queryString ? '?' + queryString : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_PARTNERS],
    }),

    getOrganizerPartnersFilters: build.query<
      GetOrganizerPartnersFiltersResponse,
      {
        filters?: {
          search?: string;
          page?: number;
          relations?: FiltersParams;
        } | null;
      }
    >({
      query: ({ filters }) => {
        const params = new URLSearchParams();
        if (filters?.search) {
          params.append('search', filters.search);
        }
        if (typeof filters?.page === 'number') {
          params.append('page', filters.page.toString());
        }

        const relations = filters?.relations;

        if (relations) {
          const relationsQuery = [
            getQueryStringFromParams(relations.events, 'events', 'relations'),
            getQueryStringFromParams(relations.campaigns, 'campaigns', 'relations'),
            getQueryStringFromParams(relations.landings, 'landings', 'relations'),
            getQueryStringFromParams(relations.partners, 'partners', 'relations'),
          ]
            .filter(Boolean)
            .join('&');

          if (relationsQuery) {
            new URLSearchParams(relationsQuery).forEach((value, key) => {
              params.append(key, value);
            });
          }
        }

        const queryString = params.toString();
        return {
          url: `/organizer/partners/filter${queryString ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_PARTNERS],
    }),
  }),
});

export const {
  useGetOrganizerPartnersByEventQuery,
  usePatchOrganizerPartnersStatusByIdMutation,
  useGetOrganizerPartnersByEventFiltersQuery,
  useGetOrganizerPartnersFiltersQuery,
} = organizerPartners;
